/**
 * FB onboarding screen renderers - one component per step `type`.
 *
 * Each receives the resolved (name-substituted) step plus wizard callbacks and
 * renders inside <FbShell>. Single-choice screens auto-advance on tap (like
 * Keiki); multi-select and text reveal a Continue button. Keep visuals in the
 * cream/orange skin from FbShell.
 */

import React, { useState, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { motion } from 'framer-motion';
import { Check, CheckCircle, XCircle, Star, GoogleLogo, SpeakerHigh } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import FbShell, { FbButton, FB, ImageSlot, getChrome, fillName } from './FbShell';
import timoYoga from '@/assets/timoyoga.webp';
import timoVoicePanama from '@/assets/TimoPanama].webp';
import timoVoiceBeany from '@/assets/TimoBeany.webp';
import timoMail from '@/assets/timo-mail.webp';
import voiceTimmyClip from '@/assets/voice-timmy.mp3';
import voiceTimoClip from '@/assets/voice-timo.mp3';
import { isInAppBrowser } from '@/lib/inAppBrowser';

// Best-effort funnel tracking (mirrors the helper in ../index.jsx). Used for the
// finer-grained interactions that live inside individual screens.
const track = (event, props) => {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics is best-effort - never break the funnel */
  }
};

// Staggered entrance for lists of option cards - each card fades/rises in turn.
const listStagger = { animate: { transition: { staggerChildren: 0.05 } } };
const cardRise = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } },
};

// Short pause so the selected-state animation is visible before advancing.
const ADVANCE_MS = 190;

function useAutoAdvance(onSelect) {
  return (value) => {
    onSelect(value);
    setTimeout(() => onSelect(value, true), ADVANCE_MS);
  };
}

/* ---------------------------------------------------------------- text ---- */
export function TextScreen({ step, value, setValue, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  return (
    <FbShell
      {...shell}
      question={step.question}
      sub={step.sub}
      footer={
        <>
          <FbButton onClick={onNext}>{t.continue}</FbButton>
          {/* min-h-44: this was a 33pt row, under the touch minimum on every
              phone in the matrix, and it is the only way past an optional step
              for anyone who does not want to answer it. */}
          {step.optional && (
            <button
              type="button"
              onClick={onNext}
              className="w-full mt-2 min-h-[44px] py-2 text-[14px] font-medium touch-manipulation"
              style={{ color: FB.inkSoft }}
            >
              {t.skip}
            </button>
          )}
        </>
      }
    >
      <input
        type="text"
        autoFocus
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onNext()}
        placeholder={step.placeholder}
        className="w-full py-4 px-5 rounded-[18px] text-[17px] font-medium bg-white border-[1.5px] border-transparent focus:outline-none focus:border-[#FF8C5A] shadow-[0_2px_10px_rgba(45,49,66,0.05)]"
        style={{ color: FB.ink }}
      />
    </FbShell>
  );
}

/* -------------------------------------------------------------- binary ---- */
export function BinaryScreen({ step, value, setValue, ...shell }) {
  const choose = useAutoAdvance((v, advance) => (advance ? shell.onNext() : setValue(v)));
  const isYesNo = step.options.every((o) => o.value === 'yes' || o.value === 'no');

  return (
    <FbShell {...shell} question={step.question} sub={step.sub}>
      <div className="flex-1 flex flex-col justify-end pb-6">
        <motion.div
          variants={listStagger}
          initial="initial"
          animate="animate"
          className="grid grid-cols-2 gap-3"
        >
          {step.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <motion.button
                key={opt.value}
                variants={cardRise}
                whileTap={{ scale: 0.97 }}
                onClick={() => choose(opt.value)}
                className={cn(
                  'aspect-[4/5] rounded-[26px] flex flex-col items-center justify-center gap-3 touch-manipulation transition-shadow',
                  selected
                    ? 'shadow-[0_8px_24px_rgba(255,140,90,0.22)]'
                    : 'shadow-[0_3px_14px_rgba(45,49,66,0.05)]'
                )}
                style={{
                  backgroundColor: selected ? FB.cardTint : '#FFFFFF',
                  border: `1.5px solid ${selected ? FB.orange : 'transparent'}`,
                }}
              >
                {isYesNo ? (
                  opt.value === 'yes' ? (
                    <CheckCircle size={60} weight="fill" color="#5FBE74" />
                  ) : (
                    <XCircle size={60} weight="fill" color="#EF8A6B" />
                  )
                ) : (
                  <span className="text-[52px] leading-none">{opt.emoji}</span>
                )}
                <span className="font-semibold text-[17px]" style={{ color: FB.ink }}>
                  {opt.label}
                </span>
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </FbShell>
  );
}

/* -------------------------------------------------------------- single ---- */
export function SingleScreen({ step, value, setValue, ...shell }) {
  const choose = useAutoAdvance((v, advance) => (advance ? shell.onNext() : setValue(v)));
  return (
    <FbShell {...shell} question={step.question} sub={step.sub}>
      {step.image !== undefined && (
        <ImageSlot src={step.image} label="illustration" className="mb-5" />
      )}
      <motion.div
        variants={listStagger}
        initial="initial"
        animate="animate"
        className="flex flex-col gap-2.5 pb-4"
      >
        {step.options.map((opt) => {
          const selected = value === opt.value;
          return (
            <motion.button
              key={opt.value}
              variants={cardRise}
              whileTap={{ scale: 0.98 }}
              onClick={() => choose(opt.value)}
              className={cn(
                'w-full py-[15px] px-5 rounded-[18px] text-left font-medium text-[16px] touch-manipulation transition-shadow flex items-center justify-between gap-3',
                selected
                  ? 'shadow-[0_6px_18px_rgba(255,140,90,0.20)]'
                  : 'shadow-[0_2px_10px_rgba(45,49,66,0.04)]'
              )}
              style={{
                backgroundColor: selected ? FB.cardTint : '#FFFFFF',
                border: `1.5px solid ${selected ? FB.orange : 'transparent'}`,
                color: FB.ink,
              }}
            >
              <span className="min-w-0">
                {opt.emoji ? <span className="mr-2">{opt.emoji}</span> : null}
                {opt.label}
              </span>
              {opt.hint ? (
                <span className="text-[14px] font-semibold flex-shrink-0" style={{ color: '#B0A79C' }}>
                  {opt.hint}
                </span>
              ) : null}
            </motion.button>
          );
        })}
      </motion.div>
    </FbShell>
  );
}

/* --------------------------------------------------------------- multi ---- */
export function MultiScreen({ step, value, setValue, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  const selected = Array.isArray(value) ? value : [];
  const atMax = step.max && selected.length >= step.max;

  const toggle = (v) => {
    if (selected.includes(v)) {
      setValue(selected.filter((x) => x !== v));
    } else if (!atMax) {
      setValue([...selected, v]);
    }
  };

  return (
    <FbShell
      {...shell}
      question={step.question}
      sub={step.sub}
      footer={
        <FbButton onClick={onNext} disabled={selected.length === 0}>
          {t.continue}
        </FbButton>
      }
    >
      <motion.div
        variants={listStagger}
        initial="initial"
        animate="animate"
        className="flex flex-col gap-2.5 pb-4"
      >
        {step.options.map((opt) => {
          const isOn = selected.includes(opt.value);
          const dimmed = !isOn && atMax;
          return (
            <motion.button
              key={opt.value}
              variants={cardRise}
              whileTap={{ scale: 0.98 }}
              onClick={() => toggle(opt.value)}
              className={cn(
                'w-full py-[15px] px-5 rounded-[18px] text-left font-medium text-[15px] flex items-center justify-between gap-3 touch-manipulation transition-all',
                isOn ? 'shadow-[0_6px_18px_rgba(255,140,90,0.20)]' : 'shadow-[0_2px_10px_rgba(45,49,66,0.04)]',
                dimmed && 'opacity-45'
              )}
              style={{
                backgroundColor: isOn ? FB.cardTint : '#FFFFFF',
                border: `1.5px solid ${isOn ? FB.orange : 'transparent'}`,
                color: FB.ink,
              }}
            >
              <span className="min-w-0">
                {opt.emoji ? <span className="mr-2">{opt.emoji}</span> : null}
                {opt.label}
              </span>
              <span
                className="w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center"
                style={{
                  backgroundColor: isOn ? FB.orange : 'transparent',
                  border: isOn ? 'none' : '2px solid rgba(45,49,66,0.18)',
                }}
              >
                {isOn && <Check size={16} weight="bold" color="#fff" />}
              </span>
            </motion.button>
          );
        })}
      </motion.div>
    </FbShell>
  );
}

/* -------------------------------------------------------- interstitial ---- */
export function InterstitialScreen({ step, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  return (
    <FbShell
      {...shell}
      showProgress={false}
      showMascot={false}
      footer={<FbButton onClick={onNext}>{step.cta || t.continue}</FbButton>}
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="flex-1 flex flex-col justify-center py-6 text-center"
      >
        {step.photo !== undefined ? (
          // Real photo (not a transparent cutout): full-width rounded card,
          // cropped square upstream so it reads well here without letterboxing.
          <ImageSlot src={step.photo} ratio="1 / 1" className="mb-5 max-w-[220px] mx-auto" />
        ) : step.image !== undefined ? (
          // Transparent mascot cutout: float it (contain, no card) so it isn't
          // cropped by ImageSlot's object-cover peach box.
          <motion.img
            src={step.image}
            alt="Timo"
            draggable="false"
            initial={{ scale: 0.85, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
            className="w-40 h-40 sm:w-48 sm:h-48 object-contain mx-auto mb-6 select-none"
          />
        ) : (
          // Colored halo behind the emoji for a livelier, less flat interstitial.
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
            className="mx-auto mb-6 w-28 h-28 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${step.accent || FB.orange}22` }}
          >
            <span className="text-[60px] leading-none">{step.emoji || '🌱'}</span>
          </motion.div>
        )}
        <h2 className="font-semibold text-[23px] leading-tight mb-3" style={{ color: FB.ink }}>
          {step.title}
        </h2>
        {step.body && (
          <p className="text-[15.5px] leading-relaxed mb-6 font-normal" style={{ color: '#6B6472' }}>
            {step.body}
          </p>
        )}
        {step.quote && (
          <div
            className="rounded-[24px] p-5 shadow-[0_4px_16px_rgba(45,49,66,0.05)] text-left"
            style={{ backgroundColor: '#FFFFFF', borderLeft: `4px solid ${step.accent || FB.orange}` }}
          >
            <p className="font-medium text-[15.5px] leading-snug" style={{ color: FB.ink }}>
              “{step.quote.text}”
            </p>
            <p className="mt-2 text-[13px] font-medium" style={{ color: step.accent || FB.orange }}>
              {step.quote.author}
            </p>
          </div>
        )}
      </motion.div>
    </FbShell>
  );
}

/* --------------------------------------------------------------- score ---- */
// Animated semicircle gauge. `f` is the current fraction (0..1); the fill and
// knob animate as it rises from 0 to the real score on mount.
function Gauge({ f }) {
  const r = 80;
  const cx = 100;
  const cy = 100;
  const len = Math.PI * r;
  const a = (1 - f) * Math.PI; // radians, sweeping over the top
  const knobX = cx + r * Math.cos(a);
  const knobY = cy - r * Math.sin(a);

  return (
    <svg viewBox="0 0 200 116" className="w-full max-w-[280px] mx-auto">
      <defs>
        <linearGradient id="fbGauge" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FF6B4A" />
          <stop offset="100%" stopColor="#FF8C5A" />
        </linearGradient>
      </defs>
      <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={FB.track} strokeWidth="14" strokeLinecap="round" />
      <path
        d="M20 100 A80 80 0 0 1 180 100"
        fill="none"
        stroke="url(#fbGauge)"
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={`${f * len} ${len}`}
      />
      <circle cx={knobX} cy={knobY} r="11" fill={FB.orange} stroke="#fff" strokeWidth="4" />
    </svg>
  );
}

export function ScoreScreen({ step, score, name, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  // Count the score + gauge up from 0 on mount for a satisfying reveal.
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const dur = 950;
    const id = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / dur);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(score * eased);
      if (t >= 1) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, [score]);

  return (
    <FbShell
      {...shell}
      showProgress={false}
      showMascot={false}
      footer={<FbButton onClick={onNext}>{step.cta}</FbButton>}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="flex-1 flex flex-col justify-center py-4"
      >
        <h2 className="font-semibold text-[23px] leading-tight text-center mb-5" style={{ color: FB.ink }}>
          {step.title}
        </h2>
        {step.image !== undefined && (
          <ImageSlot src={step.image} emoji="🦝" ratio="16 / 9" className="mb-5" />
        )}
        <Gauge f={Math.max(0, Math.min(1, shown / 100))} />
        <div className="text-center -mt-2 mb-6">
          <p className="text-[14px] font-medium" style={{ color: FB.orange }}>
            {name ? fillName(t.scoreTodayNamed, name) : t.scoreToday}
          </p>
          <p
            className="font-bold text-[54px] leading-none tabular-nums"
            style={{
              backgroundImage: 'linear-gradient(90deg, #FF5A36 0%, #FF8C5A 55%, #FFB347 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            {/* Whole numbers only. computeScore is integer arithmetic over about
                five taps, so a decimal place would always be ".0" and its only
                effect is to make a self-report look like a measured instrument. */}
            {Math.round(shown)}
          </p>
        </div>
        <p className="text-[13px] text-center mb-4 font-normal" style={{ color: '#9A93A2' }}>
          {t.scoreBasis}
        </p>
        <motion.div
          variants={listStagger}
          initial="initial"
          animate="animate"
          className="flex flex-wrap justify-center gap-2"
        >
          {step.areas.map((area) => (
            <motion.span
              key={area}
              variants={cardRise}
              className="px-4 py-2 rounded-full bg-white text-[14px] font-medium shadow-[0_2px_10px_rgba(45,49,66,0.05)]"
              style={{ color: FB.ink }}
            >
              {area}
            </motion.span>
          ))}
        </motion.div>
      </motion.div>
    </FbShell>
  );
}

/* ---------------------------------------------------------------- grid ---- */
export function GridScreen({ step, value, setValue, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) =>
    setValue(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <FbShell
      {...shell}
      question={step.question}
      sub={step.sub}
      footer={
        <FbButton onClick={onNext} disabled={selected.length === 0}>
          {t.continue}
        </FbButton>
      }
    >
      <motion.div
        variants={listStagger}
        initial="initial"
        animate="animate"
        className="grid grid-cols-2 gap-3 pb-4"
      >
        {step.options.map((opt) => {
          const isOn = selected.includes(opt.value);
          return (
            <motion.button
              key={opt.value}
              variants={cardRise}
              whileTap={{ scale: 0.97 }}
              onClick={() => toggle(opt.value)}
              className={cn(
                'relative aspect-[5/4] rounded-[24px] flex flex-col items-center justify-center gap-2 touch-manipulation transition-shadow',
                isOn ? 'shadow-[0_8px_22px_rgba(255,140,90,0.20)]' : 'shadow-[0_3px_14px_rgba(45,49,66,0.05)]'
              )}
              style={{
                backgroundColor: isOn ? FB.cardTint : '#FFFFFF',
                border: `1.5px solid ${isOn ? FB.orange : 'transparent'}`,
              }}
            >
              <span className="text-[40px] leading-none">{opt.emoji}</span>
              <span className="font-medium text-[15px]" style={{ color: FB.ink }}>
                {opt.label}
              </span>
              <span
                className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: isOn ? FB.orange : 'rgba(45,49,66,0.06)',
                }}
              >
                {isOn && <Check size={15} weight="bold" color="#fff" />}
              </span>
            </motion.button>
          );
        })}
      </motion.div>
    </FbShell>
  );
}

/* ------------------------------------------------------------- loading ---- */
export function LoadingScreen({ step, onNext, ...shell }) {
  const [pct, setPct] = useState(0);
  const [labelIdx, setLabelIdx] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    const start = Date.now();
    const total = 4600; // ms - long enough to feel like real analysis
    const id = setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / total);
      setPct(Math.round(t * 100));
      setLabelIdx(Math.min(step.labels.length - 1, Math.floor(t * step.labels.length)));
      if (t >= 1 && !doneRef.current) {
        doneRef.current = true;
        clearInterval(id);
        onNext();
      }
    }, 60);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FbShell {...shell} showProgress={false} showMascot={false} showBack={false}>
      <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
        {/* Floating Timo, ported from the old onboarding loader. */}
        <motion.img
          src={timoYoga}
          alt="Timo"
          draggable="false"
          className="w-28 mb-6"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <h2 className="font-semibold text-[21px] leading-tight mb-7" style={{ color: FB.ink }}>
          {step.title}
        </h2>
        <div className="relative w-36 h-36 mb-6">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="44" fill="none" stroke={FB.track} strokeWidth="7" />
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke={FB.orange}
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 44}
              strokeDashoffset={2 * Math.PI * 44 * (1 - pct / 100)}
              style={{ transition: 'stroke-dashoffset 60ms linear' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-semibold text-[28px]" style={{ color: FB.orange }}>
              {pct}%
            </span>
          </div>
        </div>
        <motion.p
          key={labelIdx}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="text-[15px] font-medium mb-8"
          style={{ color: '#8A8494' }}
        >
          {step.labels[labelIdx]}
        </motion.p>
        {step.review && (
          <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_16px_rgba(45,49,66,0.05)] text-left w-full">
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: step.review.stars }).map((_, i) => (
                <Star key={i} size={16} weight="fill" color="#FFC23C" />
              ))}
            </div>
            <p className="text-[15px] leading-snug font-medium" style={{ color: FB.ink }}>
              “{step.review.text}”
            </p>
            <p className="mt-2 text-[13px]" style={{ color: '#9A93A2' }}>
              {step.review.author}
            </p>
          </div>
        )}
      </div>
    </FbShell>
  );
}

/* --------------------------------------------------------------- email ---- */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailScreen({
  step,
  value,
  setValue,
  consent,
  setConsent,
  onNext,
  onGoogle,
  authBusy,
  authError,
  ...shell
}) {
  const t = getChrome(shell.lang);
  const valid = EMAIL_RE.test((value || '').trim());
  const emailFocusedRef = useRef(false);
  // Google OAuth is blocked inside the Facebook/Instagram webview, so only
  // offer it in a real browser. In the webview, email is the (working) path.
  const canGoogle = !isInAppBrowser();
  return (
    <FbShell
      {...shell}
      showProgress={false}
      showMascot={false}
      showLogin
      footer={
        <>
          <FbButton onClick={onNext} disabled={!valid || authBusy}>
            {authBusy ? t.saving : t.continue}
          </FbButton>
          <p className="mt-3 text-[11px] text-center leading-snug" style={{ color: '#A79E95' }}>
            {t.legalPrefix}{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">{t.terms}</a> {t.legalAnd}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">{t.privacy}</a>.
          </p>
        </>
      }
    >
      <div className="pt-2 pb-4">
        <ImageSlot src={timoMail} ratio="16 / 10" className="mb-5" />
        <h2 className="font-semibold text-[23px] leading-tight mb-2" style={{ color: FB.ink }}>
          {step.title}
        </h2>
        {step.sub && (
          <p className="text-[15px] leading-snug mb-5 font-normal" style={{ color: '#6B6472' }}>
            {step.sub}
          </p>
        )}

        {/* Fastest path: one-tap Google (no password, no email to verify).
            Hidden in the FB/IG webview where Google OAuth is disallowed. */}
        {canGoogle && (
          <>
            <button
              type="button"
              onClick={onGoogle}
              disabled={authBusy}
              className="w-full py-[14px] rounded-[18px] bg-white flex items-center justify-center gap-2.5 font-medium text-[16px] shadow-[0_2px_10px_rgba(45,49,66,0.06)] active:brightness-95 touch-manipulation disabled:opacity-50"
              style={{ color: FB.ink, border: '1.5px solid rgba(45,49,66,0.10)' }}
            >
              <GoogleLogo size={20} weight="bold" color="#EA4335" />
              {t.googleCta}
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ backgroundColor: FB.track }} />
              <span className="text-[12px]" style={{ color: '#B8ADA2' }}>{t.or}</span>
              <div className="flex-1 h-px" style={{ backgroundColor: FB.track }} />
            </div>
          </>
        )}

        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={value || ''}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => {
            // Reached the account step AND started typing an email: separates
            // "saw the paywall gate" from "actually engaged with it".
            if (!emailFocusedRef.current) {
              emailFocusedRef.current = true;
              track('fb_onb_email_focus');
            }
          }}
          onKeyDown={(e) => e.key === 'Enter' && valid && onNext()}
          placeholder={t.emailPlaceholder}
          className="w-full py-4 px-5 rounded-[18px] text-[17px] font-medium bg-white border-[1.5px] border-transparent focus:outline-none focus:border-[#FF8C5A] shadow-[0_2px_10px_rgba(45,49,66,0.05)]"
          style={{ color: FB.ink }}
        />
        <p className="mt-3 text-[13px] leading-snug" style={{ color: '#9A93A2' }}>
          {t.emailNote}
        </p>

        {authError && (
          <p className="mt-3 text-[13px] font-medium" style={{ color: '#E06A4A' }}>
            {authError}
          </p>
        )}
      </div>
    </FbShell>
  );
}

/* ----------------------------------------------------------- cardgrid ---- */
// Single-select shown as big 2-column cards (age, etc.). Auto-advances on tap.
export function CardGridScreen({ step, value, setValue, ...shell }) {
  const choose = useAutoAdvance((v, advance) => (advance ? shell.onNext() : setValue(v)));
  return (
    <FbShell {...shell} question={step.question} sub={step.sub}>
      <motion.div
        variants={listStagger}
        initial="initial"
        animate="animate"
        className="grid grid-cols-2 gap-3 pb-4"
      >
        {step.options.map((opt) => {
          const selected = value === opt.value;
          return (
            <motion.button
              key={opt.value}
              variants={cardRise}
              whileTap={{ scale: 0.97 }}
              onClick={() => choose(opt.value)}
              className={cn(
                'aspect-[3/2] rounded-[24px] flex flex-col items-center justify-center gap-1.5 touch-manipulation transition-shadow',
                selected ? 'shadow-[0_8px_24px_rgba(255,140,90,0.22)]' : 'shadow-[0_3px_14px_rgba(45,49,66,0.05)]'
              )}
              style={{
                backgroundColor: selected ? FB.cardTint : '#FFFFFF',
                border: `1.5px solid ${selected ? FB.orange : 'transparent'}`,
              }}
            >
              {opt.emoji && <span className="text-[30px] leading-none">{opt.emoji}</span>}
              <span className="font-semibold text-[19px]" style={{ color: FB.ink }}>
                {opt.label}
              </span>
              {opt.hint && (
                <span className="text-[12px] font-medium" style={{ color: '#B0A79C' }}>{opt.hint}</span>
              )}
            </motion.button>
          );
        })}
      </motion.div>
    </FbShell>
  );
}

/* --------------------------------------------------------------- voice ---- */
// "Choose Timo's voice." Tapping a card plays a real ElevenLabs preview clip
// (pre-generated static mp3s, so it works for unauthenticated funnel visitors -
// our /api/tts is JWT-gated) and selects that voice.
// "Timmy" and "Timo" are proper names and stay as-is in every language.
// Only the one-line description under the name is translated (descKey ->
// getChrome). Keeping the key here means the card copy stays in the chrome
// dictionary with the rest of the UI strings.
const VOICES = [
  {
    id: '9MQbSvZCz1PCthbJxqlc',
    name: 'Timmy',
    descKey: 'voiceYoung',
    avatar: timoVoicePanama,
    clip: voiceTimmyClip,
  },
  {
    id: 'TpCiT74zy2gtqQN6PG8l',
    name: 'Timo',
    descKey: 'voiceWarm',
    avatar: timoVoiceBeany,
    clip: voiceTimoClip,
  },
];

export function VoiceScreen({ step, value, setValue, onNext, ...shell }) {
  const t = getChrome(shell.lang);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const preview = (v) => {
    setValue(v.id);
    setPlaying(v.id);
    track('fb_onb_voice_preview', { voice: v.name, voice_id: v.id });
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(v.clip);
      audioRef.current = audio;
      audio.onended = () => setPlaying((p) => (p === v.id ? null : p));
      audio.onerror = () => setPlaying((p) => (p === v.id ? null : p));
      audio.play().catch(() => setPlaying((p) => (p === v.id ? null : p)));
    } catch {
      setPlaying(null);
    }
  };

  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);

  return (
    <FbShell
      {...shell}
      question={step.question}
      sub={step.sub}
      footer={
        <FbButton onClick={() => { try { audioRef.current?.pause(); } catch {} onNext(); }} disabled={!value}>
          {t.continue}
        </FbButton>
      }
    >
      <motion.div variants={listStagger} initial="initial" animate="animate" className="flex flex-col gap-3 pb-4">
        {VOICES.map((v) => {
          const selected = value === v.id;
          const isPlaying = playing === v.id;
          return (
            <motion.button
              key={v.id}
              variants={cardRise}
              whileTap={{ scale: 0.98 }}
              onClick={() => preview(v)}
              className={cn(
                'w-full rounded-[22px] p-3 flex items-center gap-3 text-left touch-manipulation transition-shadow',
                selected ? 'shadow-[0_8px_24px_rgba(255,140,90,0.22)]' : 'shadow-[0_3px_14px_rgba(45,49,66,0.05)]'
              )}
              style={{
                backgroundColor: selected ? FB.cardTint : '#FFFFFF',
                border: `1.5px solid ${selected ? FB.orange : 'transparent'}`,
              }}
            >
              <img src={v.avatar} alt={v.name} draggable="false" className="w-16 h-16 object-contain flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[18px] leading-tight" style={{ color: FB.ink }}>{v.name}</p>
                <p className="text-[13px] leading-snug" style={{ color: '#8A8494' }}>{t[v.descKey]}</p>
                {/* mini waveform */}
                <div className="flex items-center gap-[3px] h-4 mt-1.5">
                  {[0.5, 0.9, 1, 0.6, 0.8, 0.5, 0.9, 0.7].map((h, i) => (
                    <div
                      key={i}
                      className="w-[3px] rounded-full"
                      style={{
                        height: `${h * 14}px`,
                        backgroundColor: selected ? FB.orange : '#E3D8CC',
                        animation: isPlaying ? `fbwave 0.6s ease-in-out ${i * 0.06}s infinite alternate` : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: selected ? FB.orange : `${FB.orange}1A` }}
              >
                <SpeakerHigh size={18} weight="fill" color={selected ? '#fff' : FB.orange} />
              </span>
            </motion.button>
          );
        })}
      </motion.div>
      <style>{`@keyframes fbwave { from { transform: scaleY(0.5); } to { transform: scaleY(1); } }`}</style>
    </FbShell>
  );
}
